import { Component, inject, OnDestroy } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import {
    MatDialog,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from "@angular/material/dialog";
import {
    AbstractControl,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import {
    fa1,
    fa2,
    faCheckDouble,
    faEnvelope,
    faLock,
} from "@fortawesome/free-solid-svg-icons";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { NgIf } from "@angular/common";
import { Store } from "@ngrx/store";
import { RootState } from "../../../reducers/root.reducer";
import * as UserActions from "../../../actions/user.actions";
import { Subscription, take, tap } from "rxjs";
import { InfoPopupComponent } from "../info-popup/info-popup.component";
import {
    CountryISO,
    NgxIntlTelInputModule,
    PhoneNumberFormat,
    SearchCountryField,
} from "ngx-intl-tel-input";
import { Actions, ofType } from "@ngrx/effects";
import { openInfo } from "../../../actions/app.actions";
import { ComplexPhone, UserSession } from "@home-shark/types";
import { selectUser } from "../../../reducers/user.reducer";
// import { openLoginDialog } from "../../../lib/dialogs.lib";

type EditUserFormControlName = "firstName" | "lastName" | "email" | "phone";

interface EditUserForm {
    firstName: FormControl<string>;
    lastName: FormControl<string>;
    email: FormControl<string>;
    phone: FormControl<ComplexPhone>;
}

@Component({
    selector: "hs-edit-user",
    templateUrl: "./edit-user-popup.component.html",
    styleUrls: ["./edit-user-popup.component.sass"],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        FormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatDialogActions,
        MatDialogClose,
        MatDialogTitle,
        MatDialogContent,
        NgxIntlTelInputModule,
        FaIconComponent,
        NgIf,
    ],
})
export class EditUserComponent implements OnDestroy {
    public editUserForm: FormGroup<EditUserForm>;

    protected readonly oneIcon = fa1;
    protected readonly twoIcon = fa2;
    protected readonly emailIcon = faEnvelope;
    protected readonly lockIcon = faLock;
    protected readonly doubleCheckIcon = faCheckDouble;

    readonly dialogRef = inject(MatDialogRef<EditUserComponent>);

    public lastNameControl: FormControl<string>;
    public firstNameControl: FormControl<string>;
    public emailControl: FormControl<string>;
    public phoneControl: FormControl<ComplexPhone>;
    readonly dialog = inject(MatDialog);
    public infoDialog: MatDialogRef<InfoPopupComponent>;
    private signedUpSub: Subscription;
    private actions$ = inject(Actions);

    public constructor(public store: Store<RootState>) {
        this.store.dispatch(UserActions.getSelf());
        let user: UserSession;
        this.store
            .select(selectUser)
            .pipe(take(1))
            .subscribe((selectedUser) => {
                user = selectedUser;
            });
        this.initForm(user);
        this.signedUpSub = this.actions$
            .pipe(
                ofType(UserActions.signedUp),
                tap((res) => {
                    this.dialogRef.close();
                    this.store.dispatch(
                        openInfo({
                            width: "35rem",
                            height: "11rem",
                            data: {
                                title: "Sign Up Successful",
                                text: `Check your email (${res.email}) for confirmation`,
                            },
                        })
                    );
                })
            )
            .subscribe();
    }

    public initForm(user: UserSession) {
        this.firstNameControl = new FormControl<string>(user.firstName, {
            nonNullable: true,
            validators: [Validators.required],
        });
        this.lastNameControl = new FormControl<string>(user.lastName, {
            nonNullable: true,
            validators: [Validators.required],
        });
        this.emailControl = new FormControl<string>(user.email, {
            nonNullable: true,
            validators: [Validators.required, Validators.email],
        });
        this.phoneControl = new FormControl<ComplexPhone>(undefined, {
            validators: [Validators.required],
        });
        this.editUserForm = new FormGroup<EditUserForm>({
            firstName: this.firstNameControl,
            lastName: this.lastNameControl,
            email: this.emailControl,
            phone: this.phoneControl,
        });
    }

    private validatePattern<T>(
        controlOne: AbstractControl<T>
    ): ValidationErrors | null {
        const errors = Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s])[^\s]*$/g
        )(controlOne);
        return errors;
    }

    public displayControlErrors(
        signupForm: FormGroup<EditUserForm>,
        controlName: EditUserFormControlName,
        priorControlNames: Array<EditUserFormControlName> = []
    ): boolean {
        const priorControlsValidOrClean = this.validOrClean(
            signupForm,
            priorControlNames
        );
        const currentControlInvalidAndDirty = !this.validOrClean(signupForm, [
            controlName,
        ]);
        return priorControlsValidOrClean && currentControlInvalidAndDirty;
    }

    public validOrClean(
        signupForm: FormGroup<EditUserForm>,
        controlNames: Array<EditUserFormControlName> = []
    ): boolean {
        const invalidAndDirty = controlNames.find(
            (controlName: EditUserFormControlName) => {
                const control = signupForm.controls[controlName];
                if (!control) return true;
                return !control.valid && control.touched;
            }
        );
        return !invalidAndDirty;
    }

    public editUser(): void {
        this.editUserForm.markAllAsTouched();
        if (this.editUserForm.valid) {
            const values = this.editUserForm.value;
            this.store.dispatch(
                UserActions.editUser({
                    user: {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        phone: values.phone.number,
                    },
                    onSuccess: this.dialogRef.close.bind(this.dialogRef),
                })
            );
        }
    }

    // public openLogin() {
    //     if (this.infoDialog) this.infoDialog.close();
    //     this.dialogRef.close();
    //     openLoginDialog(this.dialog);
    // }

    public dirtyForm() {
        this.editUserForm.markAllAsTouched();
    }

    ngOnDestroy() {
        if (this.signedUpSub?.unsubscribe) this.signedUpSub.unsubscribe();
    }

    protected readonly CountryISO = CountryISO;
    protected readonly SearchCountryField = SearchCountryField;
    protected readonly PhoneNumberFormat = PhoneNumberFormat;
}
